import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SectionTitle from '@/components/SectionTitle';
import Seo from '@/seo/Seo';
import { PAGE_SEO } from '@/seo/seoConfig';
import { breadcrumbSchema } from '@/seo/schemas';
import { HandHeart, Home, Users, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/api/client';

export default function PrayerCell() {
  const [form, setForm] = useState({ full_name: '', email: '', request: '', is_anonymous: false });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.request.trim()) return toast.error('Please share your prayer request');
    setSubmitting(true);
    try {
      await api.post('/public/prayer-request', form);
      toast.success('Your prayer request has been received. We are praying with you.');
      setForm({ full_name: '', email: '', request: '', is_anonymous: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit. Please try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <div>
      <Seo
        title={PAGE_SEO.prayerCell.title}
        titleExact
        description={PAGE_SEO.prayerCell.description}
        keywords={PAGE_SEO.prayerCell.keywords}
        path="/services/prayer-cell"
        jsonLd={[breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: 'Prayer Cell', path: '/services/prayer-cell' },
        ])]}
      />
      <section className="gradient-purple text-white py-12 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Link to="/services" className="text-moj-gold text-sm hover:underline">← Back to all services</Link>
          <h1 className="font-serif text-4xl sm:text-5xl text-white mt-3 mb-3">Prayer Cell</h1>
          <p className="text-lg text-white/90 max-w-3xl">Prayer Cell gatherings create space for worship, intercession, encouragement, healing prayer, and spiritual growth through the Word of God.</p>
        </div>
      </section>

      <section className="py-16 bg-moj-cream">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-3 gap-6">
          <div className="card-moj p-6">
            <Sparkles size={28} className="text-moj-gold-deep mb-2"/>
            <h3 className="font-serif text-lg mb-1">Weekly Gatherings</h3>
            <p className="text-moj-charcoal/80 text-sm">Wednesdays at 19:30 — a midweek refuelling of the Spirit at our main hall.</p>
          </div>
          <div className="card-moj p-6">
            <Home size={28} className="text-moj-gold-deep mb-2"/>
            <h3 className="font-serif text-lg mb-1">Home Prayer Groups</h3>
            <p className="text-moj-charcoal/80 text-sm">Smaller groups meet in homes across Dublin throughout the week. Ask us to connect you with one near you.</p>
          </div>
          <div className="card-moj p-6">
            <Users size={28} className="text-moj-gold-deep mb-2"/>
            <h3 className="font-serif text-lg mb-1">Testimonies</h3>
            <p className="text-moj-charcoal/80 text-sm">Time and again we see God answer prayer — healing, salvation, restoration, breakthrough. Come and see.</p>
          </div>
        </div>
      </section>

      <section className="py-16 gradient-soft-cream">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <SectionTitle kicker="Prayer Request" title="Share what’s on your heart" center
            subtitle="Our intercessors will pray with confidence in the Name of Jesus. Your request is treated with care and confidentiality."/>
          <form onSubmit={onSubmit} className="card-moj p-6 sm:p-8 space-y-4" data-testid="prayer-cell-form">
            <div>
              <label className="label-moj" htmlFor="pname">Full Name (optional)</label>
              <input id="pname" className="input-moj" value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} placeholder="Your name"/>
            </div>
            <div>
              <label className="label-moj" htmlFor="pemail">Email (optional)</label>
              <input id="pemail" type="email" className="input-moj" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="name@example.com"/>
            </div>
            <div>
              <label className="label-moj" htmlFor="preq">Prayer Request <span className="text-red-600">*</span></label>
              <textarea id="preq" required rows={5} className="input-moj" value={form.request} onChange={(e) => setForm({...form, request: e.target.value})} placeholder="Lord, please..." />
            </div>
            <label className="flex items-center gap-2 text-sm text-moj-charcoal">
              <input type="checkbox" checked={form.is_anonymous} onChange={(e) => setForm({...form, is_anonymous: e.target.checked})}/>
              Keep my request anonymous
            </label>
            <button disabled={submitting} className="btn-primary w-full justify-center" data-testid="prayer-submit"><HandHeart size={18}/> {submitting ? 'Sending…' : 'Submit Prayer Request'}</button>
          </form>
        </div>
      </section>
    </div>
  );
}
